'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Script from 'next/script';
import { faqs } from '@/app/how-it-works/data/faqs';
import PricingCalculator from './components/PricingCalculator';

export default function PricingPage() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = heroRef.current?.querySelectorAll('[data-hero]');
    els?.forEach((el, i) => {
      const element = el as HTMLElement;
      element.style.animation = `animationIn 1s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s forwards`;
    });
  }, []);

  const pricingSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'EarnestBridge Pricing',
    url: process.env.NEXT_PUBLIC_SITE_URL + '/pricing' || 'https://earnestbridge.com/pricing',
    description: 'Transparent EMD financing pricing. $2,500 administrative fee plus $100 per day per $100K financed. No hidden charges.',
    dateModified: new Date().toISOString(),
  };

  const priceSpecificationSchema = {
    '@context': 'https://schema.org',
    '@type': 'PriceSpecification',
    priceCurrency: 'USD',
    price: '2500',
    description: 'Administrative fee: $2,500 flat fee per deal. Deal fee: $100 per day per $100,000 financed.',
  };

  const pricingFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes('fee') || 
    faq.question.toLowerCase().includes('cost') || 
    faq.question.toLowerCase().includes('price') ||
    faq.question.toLowerCase().includes('repayment')
  );

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pricingFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <main className="relative overflow-x-hidden" style={{ background: 'var(--color-bg)' }}>
      <Script
        id="schema-pricing-webpage"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pricingSchema),
        }}
      />
      <Script
        id="schema-price-specification"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(priceSpecificationSchema),
        }}
      />
      <Script
        id="schema-pricing-faq"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />
      {/* Grid overlay */}
      <div className="grid-overlay">
        <div className="grid-inner">
          {[...Array(5)].map((_, i) =>
          <div key={i} className="grid-line-v" />
          )}
        </div>
      </div>

      <Header />

      {/* ── Hero ── */}
      <section className="relative pt-36 pb-16 px-6 overflow-hidden" id="pricing-hero" aria-label="Pricing overview">
        {/* Ambient glow */}
        <div
          className="absolute top-[-80px] right-[-100px] w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(82,183,136,0.08) 0%, transparent 70%)'
          }} />

        <div
          className="absolute bottom-0 left-[-80px] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(27,67,50,0.06) 0%, transparent 70%)'
          }} />


        <div className="max-w-6xl mx-auto" ref={heroRef}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            {/* Left: Heading */}
            <div className="flex-1" data-hero>
              <span
                className="inline-block text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-6"
                style={{
                  background: 'rgba(82,183,136,0.12)',
                  color: 'var(--color-accent)'
                }}>
                Transparent Pricing
              </span>
              <h1
                className="font-display leading-[1.05] mb-6"
                style={{
                  fontSize: 'clamp(2.8rem, 6vw, 5rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.03em'
                }}>
                One Upfront Payment.<br />
                <span style={{ color: 'var(--color-accent)' }}>No Surprises.</span>
              </h1>
            </div>

            {/* Right: Subtitle */}
            <div className="lg:max-w-sm" data-hero>
              <p
                className="text-lg leading-relaxed"
                style={{ color: 'var(--color-muted)' }}>Upfront payment includes: Admin + Deal Fee. No hidden layers or surprises. You know the full cost before signing.

              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bento Grid ── */}
      <section className="px-6 pb-6" id="pricing-breakdown" aria-label="Pricing breakdown">
        <div className="max-w-6xl mx-auto">
          {/* Row 1: Two main fee cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

            {/* Admin Fee — large dark card */}
            <BentoCard delay={0} className="md:col-span-1">
              <div
                className="rounded-2xl p-8 md:p-10 h-full flex flex-col justify-between min-h-[220px] relative overflow-hidden"
                style={{
                  background: 'var(--color-primary-dark)',
                  border: '1px solid rgba(82,183,136,0.12)'
                }}>

                <GridPattern />
                <div className="relative z-10">
                  <span
                    className="inline-block text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-6"
                    style={{
                      background: 'rgba(82,183,136,0.15)',
                      color: 'var(--color-accent-light)'
                    }}>

                    Administrative Fee
                  </span>
                  <div className="flex items-end gap-4">
                    <p
                      className="font-display leading-none"
                      style={{
                        fontSize: 'clamp(3.5rem, 8vw, 6rem)',
                        color: 'white',
                        letterSpacing: '-0.04em'
                      }}>

                      $2,500
                    </p>
                    <p
                      className="text-[13px] font-semibold uppercase tracking-[0.18em] mb-3"
                      style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.3 }}>
                      Flat fee ·<br />Charged per deal
                    </p>
                  </div>
                </div>
              </div>
            </BentoCard>

            {/* Deal Fee — accent card */}
            <BentoCard delay={80} className="md:col-span-1">
              <div
                className="rounded-2xl p-8 md:p-10 h-full flex flex-col justify-between min-h-[260px] relative overflow-hidden"
                style={{
                  background: 'var(--color-primary)',
                  border: '1px solid rgba(82,183,136,0.2)'
                }}>

                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                    'radial-gradient(ellipse at top right, rgba(82,183,136,0.15) 0%, transparent 60%)'
                  }} />

                <div className="relative z-10">
                  <span
                    className="inline-block text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-6"
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.7)'
                    }}>

                    Deal Fee
                  </span>
                  <div className="flex items-end gap-4">
                    <p
                      className="font-display leading-none"
                      style={{
                        fontSize: 'clamp(3.5rem, 8vw, 6rem)',
                        color: 'white',
                        letterSpacing: '-0.04em'
                      }}>

                      $100
                    </p>
                    <p
                      className="text-[13px] font-semibold uppercase tracking-[0.18em] mb-3"
                      style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.3 }}>PER DAY - PER $100K FINANCED 

                    </p>
                  </div>
                </div>
              </div>
            </BentoCard>
          </div>

          {/* Row 2: Three smaller info cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">

            {/* Collateral */}
            <BentoCard delay={160}>
              <div
                className="rounded-2xl p-7 h-full flex flex-col justify-between min-h-[160px]"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)'
                }}>

                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em]"
                  style={{ color: 'var(--color-muted)' }}>

                  Collateral
                </p>
                <div>
                  <p
                    className="font-display text-4xl leading-none mb-2"
                    style={{ color: 'var(--color-foreground)', letterSpacing: '-0.03em' }}>

                    None
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    No pledged assets required
                  </p>
                </div>
              </div>
            </BentoCard>

            {/* Prepayment */}
            <BentoCard delay={220}>
              <div
                className="rounded-2xl p-7 h-full flex flex-col justify-between min-h-[160px]"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)'
                }}>

                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em]"
                  style={{ color: 'var(--color-muted)' }}>DEBT OBLIGATION


                </p>
                <div>
                  <p
                    className="font-display text-4xl leading-none mb-2"
                    style={{ color: 'var(--color-foreground)', letterSpacing: '-0.03em' }}>

                    None
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>No repayment obligation unless option exercised

                  </p>
                </div>
              </div>
            </BentoCard>

            {/* Personal Guarantee */}
            <BentoCard delay={280}>
              <div
                className="rounded-2xl p-7 h-full flex flex-col justify-between min-h-[160px]"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)'
                }}>

                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em]"
                  style={{ color: 'var(--color-muted)' }}>

                  Personal Guarantee
                </p>
                <div>
                  <p
                    className="font-display text-4xl leading-none mb-2"
                    style={{ color: 'var(--color-foreground)', letterSpacing: '-0.03em' }}>

                    None
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    No personal liability
                  </p>
                </div>
              </div>
            </BentoCard>
          </div>

        </div>
      </section>

      {/* ── Dynamic Pricing Calculator ── */}
      <PricingCalculator />

      {/* ── Example Cost ── */}
      <section className="px-6 pb-6" aria-label="Example cost">
        <div className="max-w-6xl mx-auto">
          <BentoCard delay={340} className="w-full">
            <div
              className="rounded-2xl p-8 md:p-10 relative overflow-hidden dot-pattern"
              style={{
                background: 'var(--color-bg-alt)',
                border: '1px solid var(--color-border)'
              }}>

              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                {/* Left: label + headline */}
                <div className="lg:w-1/3 flex flex-col justify-between">
                  <div>
                    <p className="section-label mb-4">Example Cost</p>
                    <h2
                      className="font-display"
                      style={{
                        fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
                        color: 'var(--color-foreground)',
                        letterSpacing: '-0.025em',
                        lineHeight: 1.1
                      }}>
                      $250K EMD held for 30 days
                    </h2>
                    <div className="mt-6 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>EMD Funded:</span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>$250,000</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>Due Diligence Period:</span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>30 days</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: breakdown table */}
                <div className="lg:w-2/3">
                  <div className="flex flex-col gap-0">
                    {[
                    {
                      item: 'Administrative Fee',
                      cost: '$2,500',
                      note: 'One-time, per deal',
                      bold: false
                    },
                    {
                      item: 'Deal Fee (30 days)',
                      cost: '$7,500',
                      note: '$250/day × 30 days',
                      bold: false
                    },
                    {
                      item: 'Total Cost',
                      cost: '$10,000',
                      note: 'All-in, no hidden fees',
                      bold: true
                    }].
                    map((row, i, arr) =>
                    <div
                      key={row.item}
                      className="flex items-center justify-between py-5"
                      style={{
                        borderBottom:
                        i < arr.length - 1 ?
                        '1px solid var(--color-border)' :
                        'none'
                      }}>

                        <div>
                          <p
                          className={`text-sm ${row.bold ? 'font-bold' : 'font-medium'}`}
                          style={{ color: 'var(--color-foreground)' }}>

                            {row.item}
                          </p>
                          <p
                          className="text-xs mt-0.5"
                          style={{ color: 'var(--color-muted)' }}>

                            {row.note}
                          </p>
                        </div>
                        <p
                        className={`font-display ${row.bold ? 'text-3xl' : 'text-2xl'}`}
                        style={{
                          color: row.bold ?
                          'var(--color-primary)' :
                          'var(--color-foreground)',
                          letterSpacing: '-0.02em'
                        }}>

                          {row.cost}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </BentoCard>
        </div>
      </section>

      {/* ── Option Fee ── */}
      <section className="px-6 pb-6" aria-label="Option fee">
        <div className="max-w-6xl mx-auto">
          <BentoCard delay={400} className="w-full">
            <div
              className="rounded-2xl p-8 md:p-10 relative overflow-hidden"
              style={{
                background: '#1a2744',
                border: '1px solid rgba(82,183,136,0.2)'
              }}>
              <GridPattern />
              <div className="relative z-10">
                {/* Header row */}
                <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-16 mb-8">
                  <div className="lg:w-auto">
                    <span
                      className="inline-block text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-5"
                      style={{
                        background: 'rgba(82,183,136,0.15)',
                        color: 'var(--color-accent-light)'
                      }}>
                      Option Fee
                    </span>
                    <h2
                      className="font-display text-white"
                      style={{
                        fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                        color: '#ffffff',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2
                      }}>
                      Due Before Option Period Expires
                    </h2>
                  </div>
                  <div className="lg:flex-1">
                    <p
                      className="text-sm md:text-base text-white"
                      style={{ lineHeight: 1.8 }}>
                      The Option Fee (matching the EarnestBridge-funded EMD) is due before the Option Period ends. The Option Period expires <strong style={{ color: 'var(--color-accent-light)' }}>two days prior</strong> to the end of your Due Diligence Period.
                    </p>
                  </div>
                </div>

                {/* ── Timeline Visual Tracker ── */}
                <OptionFeeTimeline />
              </div>
            </div>
          </BentoCard>
        </div>
      </section>

      {/* ── Extension Fee ── */}
      <section className="px-6 pb-6" aria-label="Extension fee">
        <div className="max-w-6xl mx-auto">
          <BentoCard delay={440} className="w-full">
            <div
              className="rounded-2xl p-8 md:p-10 relative overflow-hidden"
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)'
              }}>
              <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-16">
                {/* Left: label + headline */}
                <div className="lg:w-1/3">
                  <span
                    className="inline-block text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-5"
                    style={{
                      background: 'rgba(82,183,136,0.12)',
                      color: 'var(--color-accent)'
                    }}>
                    Extension Fee
                  </span>
                  <h2
                    className="font-display"
                    style={{
                      fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                      color: 'var(--color-foreground)',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2
                    }}>
                    Extending Your Option Period
                  </h2>
                </div>

                {/* Right: description + fee breakdown */}
                <div className="lg:flex-1">
                  <p
                    className="text-sm md:text-base mb-6"
                    style={{ color: 'var(--color-muted)', lineHeight: 1.8 }}>
                    If you need to extend the option period in conjunction with a due diligence period extension, additional fees apply per extension.
                  </p>
                  <div className="flex flex-col gap-0">
                    {[
                      {
                        item: 'Additional Deal Fee',
                        cost: '$100/day per $100K financed',
                        note: 'Same rate as the standard Deal Fee, applied to the extension period',
                      },
                      {
                        item: 'Extension Fee',
                        cost: '$1,000',
                        note: 'Flat fee charged per extension',
                      },
                    ].map((row, i, arr) => (
                      <div
                        key={row.item}
                        className="flex items-start justify-between py-5"
                        style={{
                          borderBottom: i < arr.length - 1 ? '1px solid var(--color-border)' : 'none',
                        }}>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>
                            {row.item}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted)' }}>
                            {row.note}
                          </p>
                        </div>
                        <p
                          className="font-display text-xl ml-6 shrink-0"
                          style={{ color: 'var(--color-primary)', letterSpacing: '-0.02em' }}>
                          {row.cost}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </BentoCard>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="px-6 py-16 pb-28">
        <div className="max-w-6xl mx-auto">
          <div
            className="rounded-3xl overflow-hidden relative"
            style={{ background: 'var(--color-primary-dark)' }}>

            {/* Dot pattern overlay */}
            <div
              className="absolute inset-0 opacity-10 pointer-events-none"
              style={{
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)',
                backgroundSize: '24px 24px'
              }} />

            <div
              className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
              style={{
                background:
                'radial-gradient(circle, rgba(82,183,136,0.12) 0%, transparent 70%)'
              }} />


            <div className="relative z-10 p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="max-w-xl">
                <p className="section-label mb-4" style={{ color: '#74C69D' }}>
                  Ready to Move?
                </p>
                <h2
                  className="font-display mb-4"
                  style={{
                    fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                    color: 'white',
                    letterSpacing: '-0.025em',
                    lineHeight: 1.05
                  }}>

                  Know the cost.
                  <br />
                  <span style={{ color: 'var(--color-accent-light)' }}>Submit the deal.</span>
                </h2>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: 'rgba(255,255,255,0.55)' }}>

                  No surprises, no fee negotiations. Submit your deal and get a funding
                  decision within 2–4 business hours.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0">
                <Link
                  href="/contact"
                  className="btn-accent text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full text-center">

                  Submit a Deal
                </Link>
                <Link
                  href="/emd-program"
                  className="text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full text-center transition-all duration-300 hover:bg-white/10"
                  style={{
                    border: '1.5px solid rgba(255,255,255,0.2)',
                    color: 'white'
                  }}>

                  View EMD Program
                </Link>
              </div>
            </div>
          </div>

          {/* Related reading strip */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                href: '/blog/the-40000-difference-why-equity-splits-are-killing-your-scalability',
                label: 'Deal Economics',
                title: 'The $40,000 Difference vs. Equity Splits',
                sub: 'See the math on flat-fee vs. profit-share →',
              },
              {
                href: '/blog/what-is-earnest-money-deposit-financing',
                label: 'EMD Basics',
                title: 'What Is EMD Financing?',
                sub: 'Understand the full program before you apply →',
              },
              {
                href: '/blog/how-earnest-money-deposit-financing-works',
                label: 'How It Works',
                title: 'Step-by-Step: From LOI to Funded',
                sub: 'The exact process, start to finish →',
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid rgba(27,67,50,0.1)'
                }}>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--color-primary)' }}>
                  {item.label}
                </p>
                <p className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors" style={{ color: 'var(--color-foreground)' }}>
                  {item.title}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  {item.sub}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'EarnestBridge Pricing — Transparent EMD Financing Fees',
            url: `${process.env.NEXT_PUBLIC_SITE_URL || 'https://earnestbridge.com'}/pricing`,
            description: 'EarnestBridge charges a flat $2,500 Administrative Fee plus a Deal Fee of $100 per day per $100,000 financed. No collateral, no debt obligation, no personal guarantee.',
            dateModified: '2026-04-23',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: process.env.NEXT_PUBLIC_SITE_URL || 'https://earnestbridge.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Pricing',
                item: `${process.env.NEXT_PUBLIC_SITE_URL || 'https://earnestbridge.com'}/pricing`,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'EarnestBridge EMD Financing',
            provider: {
              '@type': 'Organization',
              name: 'EarnestBridge',
              url: process.env.NEXT_PUBLIC_SITE_URL || 'https://earnestbridge.com',
            },
            description: 'Earnest money deposit financing for deal-makers across commercial real estate, M&A, aviation, government contracting, oil & gas, and maritime.',
            offers: [
              {
                '@type': 'Offer',
                name: 'Administrative Fee',
                price: '2500',
                priceCurrency: 'USD',
                description: 'Flat administrative fee of $2,500 per deal for document preparation and processing.',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  price: '2500',
                  priceCurrency: 'USD',
                  valueAddedTaxIncluded: false,
                },
              },
              {
                '@type': 'Offer',
                name: 'Deal Fee',
                description: '$100 per day per $100,000 of EMD financed. Covers cost of capital for the deposit duration.',
                priceCurrency: 'USD',
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: '100',
                  priceCurrency: 'USD',
                  unitText: 'per day per $100,000 financed',
                },
              },
            ],
          }),
        }}
      />
    </main>);

}

/* ── Sub-components ── */

function BentoCard({
  children,
  delay,
  className = ''




}: {children: React.ReactNode;delay: number;className?: string;}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0)';
          ref.current.style.filter = 'blur(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: 0,
        transform: 'translateY(24px)',
        filter: 'blur(4px)',
        transition: `all 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}ms`
      }}>

      {children}
    </div>);

}

function GridPattern() {
  return (
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage:
        'linear-gradient(rgba(82,183,136,1) 1px, transparent 1px), linear-gradient(90deg, rgba(82,183,136,1) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />);
}

function OptionFeeTimeline() {
  // Example: 30-day due diligence period, option fee due at day 28 (2 days prior)
  const totalDays = 30;
  const optionDeadlineDay = totalDays - 2;
  // Buffer zone is always rendered at a fixed min-width of 52px so -2d is always visible.
  // The option segment takes the remaining space via flex-grow.
  const BUFFER_MIN_PX = 52;

  return (
    <div
      className="rounded-2xl p-6 md:p-8"
      style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(82,183,136,0.12)' }}>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-5 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ background: 'rgba(82,183,136,0.55)' }} />
          <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>Due Diligence Period (30 days)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ background: 'rgba(201,168,76,0.7)' }} />
          <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>Option Fee Deadline (Day {optionDeadlineDay})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ background: 'rgba(239,68,68,0.55)' }} />
          <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>2-Day Buffer Zone</span>
        </div>
      </div>

      {/* Day markers — flex row so Day 28 and Day 30 never overlap */}
      <div className="flex items-center mb-1" style={{ height: '16px' }}>
        {/* Day 1 — left edge */}
        <span
          className="text-[10px] font-semibold uppercase tracking-wider shrink-0"
          style={{ color: 'rgba(255,255,255,0.35)' }}>
          Day 1
        </span>
        {/* Flex spacer — grows to fill the option segment width */}
        <div className="flex-1 min-w-0" />
        {/* Day 28 — aligns with the dashed boundary between option and buffer */}
        <span
          className="text-[10px] font-semibold uppercase tracking-wider shrink-0 text-right"
          style={{
            color: 'rgba(255,255,255,0.35)',
            marginRight: '4px',
          }}>
          Day {optionDeadlineDay}
        </span>
        {/* Day 30 — always at far right, same width as buffer zone */}
        <span
          className="text-[10px] font-semibold uppercase tracking-wider shrink-0 text-right"
          style={{
            color: 'rgba(255,255,255,0.35)',
            width: `${BUFFER_MIN_PX}px`,
          }}>
          Day {totalDays}
        </span>
      </div>

      {/* Layer 1: Due Diligence full bar */}
      <div className="relative mb-2">
        <div className="w-full rounded-full overflow-hidden" style={{ height: '28px', background: 'rgba(255,255,255,0.06)' }}>
          <div
            className="h-full flex items-center"
            style={{
              width: '100%',
              background: 'rgba(82,183,136,0.22)',
              borderRadius: '9999px',
            }}>
            <span
              className="text-[10px] font-bold uppercase tracking-wider pl-4"
              style={{ color: 'rgba(82,183,136,0.9)' }}>
              Due Diligence Period — {totalDays} days
            </span>
          </div>
        </div>
      </div>

      {/* Layer 2: Option Period (flex-grow) + Buffer Zone (fixed min-width) */}
      <div className="relative mb-4">
        <div
          className="w-full rounded-full overflow-hidden flex"
          style={{ height: '28px', background: 'rgba(255,255,255,0.04)' }}>
          {/* Option period segment — takes all remaining space */}
          <div
            className="h-full flex items-center justify-start pl-3 min-w-0"
            style={{
              flex: '1 1 0%',
              background: 'rgba(201,168,76,0.35)',
              borderRight: '2px dashed rgba(201,168,76,0.7)',
            }}>
            <span
              className="text-[10px] font-bold uppercase tracking-wider hidden sm:block"
              style={{ color: 'rgba(201,168,76,1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Option Fee Due → Day {optionDeadlineDay}
            </span>
            <span
              className="text-[10px] font-bold uppercase tracking-wider sm:hidden"
              style={{ color: 'rgba(201,168,76,1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Due D{optionDeadlineDay}
            </span>
          </div>
          {/* Buffer zone — fixed width so −2d label is always visible */}
          <div
            className="h-full flex items-center justify-center shrink-0"
            style={{
              width: `${BUFFER_MIN_PX}px`,
              background: 'rgba(239,68,68,0.35)',
            }}>
            <span
              className="text-[10px] font-bold"
              style={{ color: 'rgba(239,100,100,1)', whiteSpace: 'nowrap' }}>
              −2d
            </span>
          </div>
        </div>
      </div>

      {/* Callout strip */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div
          className="flex-1 rounded-xl px-4 py-3 flex items-start gap-3"
          style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5 shrink-0">
            <circle cx="8" cy="8" r="7.5" stroke="rgba(201,168,76,0.8)" />
            <path d="M8 5v4M8 11v.5" stroke="rgba(201,168,76,1)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <span className="font-semibold" style={{ color: 'rgba(201,168,76,1)' }}>Option Fee deadline</span> is Day {optionDeadlineDay} — two full days before your due diligence period ends.
          </p>
        </div>
        <div
          className="flex-1 rounded-xl px-4 py-3 flex items-start gap-3"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5 shrink-0">
            <circle cx="8" cy="8" r="7.5" stroke="rgba(239,68,68,0.7)" />
            <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="rgba(239,100,100,1)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <span className="font-semibold" style={{ color: 'rgba(239,100,100,1)' }}>Days {optionDeadlineDay + 1}–{totalDays}</span> are the buffer zone — the option has already expired and escrow deposit returns to Earnest Bridge.
          </p>
        </div>
      </div>
    </div>
  );
}